import React, { useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";
import { useAuthContext } from "../../../AuthContextWrapper";
import { Estate } from "../../../models/Estate";
import { OUTCOMES } from "../../../constants";
import {
  MultiSelectButtonForm,
  SelectButton,
} from "../shared/MultiSelectButtonForm";
import {
  AccountsCollectionsIcon,
  AssetsMultipleStatesIcon,
  BusinessesIcon,
  InsuranceAccountsIcon,
  InvestmentBrokerageIcon,
  JeweleryIcon,
  NoneOfTheseAssetsIcon,
  RentalRealEstateIcon,
  RetirementAccountIcon,
  TrustIcon,
  VehiclesIcon,
} from "../shared/LineIcons";
import { enumToStringDictionary } from "@theatticusapp/atticus-shared-components";

export enum Outcomes {
  rentalRE = "Rental real estate property (non-primary residence)",
  investment = "Investment / brokerage accounts",
  retirement = "Retirement accounts",
  business = "Own one or more businesses",
  multiStateAssets = "Assets in multiple states",
  // highValueEstate = "Total estate assets value over {$XYZk}",
  jewelry = "Jewelry",
  insuranceAccounts = "Insurance accounts",
  vehicles = "Vehicles (e.g. boat, plane, car, tractor)",
  collections = "Accounts in collections",
  trust = "There is a trust",
  none = "None of these assets",
}

const iconStyles = { width: 60, height: 60 } as const;
const iconViewBox = "0 0 60 60";

const icons: { [key: string]: JSX.Element } = {
  rentalRE: <RentalRealEstateIcon sx={iconStyles} viewBox={iconViewBox} />,
  investment: <InvestmentBrokerageIcon sx={iconStyles} viewBox={iconViewBox} />,
  retirement: <RetirementAccountIcon sx={iconStyles} viewBox={iconViewBox} />,
  business: <BusinessesIcon sx={iconStyles} viewBox={iconViewBox} />,
  multiStateAssets: (
    <AssetsMultipleStatesIcon sx={iconStyles} viewBox={iconViewBox} />
  ),
  // highValueEstate: <InvestmentBrokerageIcon sx={iconStyles} viewBox={iconViewBox} />,
  jewelry: <JeweleryIcon sx={iconStyles} viewBox={iconViewBox} />,
  insuranceAccounts: (
    <InsuranceAccountsIcon sx={iconStyles} viewBox={iconViewBox} />
  ),
  vehicles: <VehiclesIcon sx={iconStyles} viewBox={iconViewBox} />,
  collections: (
    <AccountsCollectionsIcon sx={iconStyles} viewBox={iconViewBox} />
  ),
  trust: <TrustIcon sx={iconStyles} viewBox={iconViewBox} />,
  none: <NoneOfTheseAssetsIcon sx={iconStyles} viewBox={iconViewBox} />,
};

const outcomesDictionary = enumToStringDictionary(Outcomes);
const assetSelectButtons: SelectButton[] = [];
Object.keys(outcomesDictionary).forEach((key: string) =>
  assetSelectButtons.push({
    key,
    text: outcomesDictionary[key],
    icon: icons[key],
  })
);

export const EstateAssetsScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { flow, activeEstate, setActiveEstate } = useAuthContext()
  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);

  const handleComplete = async (checked: string[]): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    const estate = activeEstate || new Estate();
    estate.typeOfAssets = checked;
    // support legacy asset recording
    estate.hasInvestmentAccounts = checked.includes("investment");
    estate.hasLifeInsurance = checked.includes("insuranceAccounts");
    estate.hasBusinesses = checked.includes("business");
    estate.hasOutOfStateAssets = checked.includes("multiStateAssets");
    estate.hasTrust = checked.includes("trust");

    try {
      await estate.saveEstate();
      setActiveEstate(estate);
    } catch (error) {
      throw new Error("could not save data");
    }

    flow?.navigateToNext(currentScreen, OUTCOMES.complete, navigate);
    return true;
  };

  return currentScreen ? (
    <FlowScreenWrapper
      title="Does the estate include any of the following assets?"
      subTitle="This helps us determine the correct forms and procedures required to settle the estate correctly. Don’t worry—you can always change your answers later."
    >
      <MultiSelectButtonForm
        initChecked={activeEstate?.typeOfAssets || undefined}
        selectButtons={assetSelectButtons}
        isOneRequired={true}
        noneOfAboveButtonKey="none"
        onComplete={handleComplete}
        onBack={() => navigate(-1)}
      />
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
